/**
 * useTemplateAutomation
 * ----------------------
 * Fetches workflow automation data for a practice and maps it to the
 * WhatsApp templates that each workflow sends. Designed to be called once
 * at the template-selector level so all list items share a single request.
 *
 * Returns:
 *   hintMap   {object}  — { templateName: [{workflow_id, is_enabled, trigger_event, workflow_name}] }
 *   loading   {boolean} — true while the initial fetch is in flight
 *   setEnabled(workflowId, newEnabled) — optimistic PATCH; reverts + toasts on failure
 *
 * Module-level Promise cache ensures one fetch per practiceId per page load,
 * even when multiple components call the hook simultaneously.
 */
import { useState, useEffect, useCallback } from 'react';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';

// ---------------------------------------------------------------------------
// Trigger-event → plain-English label
// ---------------------------------------------------------------------------

export const TRIGGER_LABELS = {
    lead_created:              'new enquiry comes in',
    lead_restored:             'lead is restored',
    appointment_confirmed:     'appointment is confirmed',
    appointment_attended:      'consultation is attended',
    appointment_cancelled:     'appointment is cancelled',
    consultation_attended:     'consultation is attended',
    treatment_plan_created:    'treatment plan is created',
    treatment_plan_accepted:   'treatment plan is accepted',
    treatment_started:         'treatment starts',
    callback_requested:        'callback is requested',
    feedback_requested:        'feedback is requested',
    deposit_paid:              'deposit is paid',
    invoice_paid:              'invoice is paid',
    lead_status_changed:       'lead status changes',
};

export function triggerLabel(triggerEvent) {
    return TRIGGER_LABELS[triggerEvent] || (triggerEvent || '').replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Module-level fetch cache — one Promise<rawWorkflows[]> per practiceId
// ---------------------------------------------------------------------------

const _fetchCache = {};

function fetchWorkflows(practiceId) {
    if (!practiceId) return Promise.resolve([]);
    if (!_fetchCache[practiceId]) {
        _fetchCache[practiceId] = callApi(
            `/api/v1/practices/${practiceId}/workflows/`,
            'GET'
        ).then((res) => {
            if (!res.ok) return [];
            return Array.isArray(res.data) ? res.data : (res.data?.results || []);
        }).catch(() => []);
    }
    return _fetchCache[practiceId];
}

// ---------------------------------------------------------------------------
// Build { templateName: [{workflow_id, is_enabled, trigger_event, workflow_name}] }
// ---------------------------------------------------------------------------

function buildHintMap(workflows) {
    const map = {};
    (Array.isArray(workflows) ? workflows : []).forEach((wf) => {
        (wf.workflow_steps || []).forEach((step) => {
            if (step.action !== 'send_whatsapp_template_message') return;
            const tplName = step.params?.template_id;
            if (!tplName) return;
            if (!map[tplName]) map[tplName] = [];
            map[tplName].push({
                workflow_id:    wf.id,
                workflow_name:  wf.name || '',
                trigger_event:  wf.trigger_event || '',
                is_enabled:     wf.is_enabled !== false,
            });
        });
    });
    return map;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export default function useTemplateAutomation(practiceId) {
    const [hintMap, setHintMap] = useState({});
    const [loading, setLoading] = useState(true);
    // Set of workflowIds currently being PATCH'd (disables their switches)
    const [inflight, setInflight] = useState(new Set());

    useEffect(() => {
        if (!practiceId) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        fetchWorkflows(practiceId).then((workflows) => {
            if (cancelled) return;
            setHintMap(buildHintMap(workflows));
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [practiceId]);

    /**
     * Optimistic toggle: update hintMap immediately, PATCH to server,
     * revert on failure.
     */
    const setEnabled = useCallback(async (workflowId, newEnabled) => {
        if (!practiceId || !workflowId) return;

        // Mark in-flight
        setInflight((prev) => new Set([...prev, workflowId]));

        // Optimistic update
        const prevMap = hintMap;
        setHintMap((prev) => {
            const next = {};
            Object.entries(prev).forEach(([tplName, hints]) => {
                next[tplName] = hints.map((h) =>
                    h.workflow_id === workflowId ? { ...h, is_enabled: newEnabled } : h
                );
            });
            return next;
        });

        const res = await callApi(
            `/api/v1/practices/${practiceId}/workflows/${workflowId}/`,
            'PATCH',
            { is_enabled: newEnabled }
        );

        if (res.ok) {
            // Invalidate the fetch cache so next open gets fresh data
            delete _fetchCache[practiceId];
            dispatch(updateError({
                message: `Automation ${newEnabled ? 'enabled' : 'disabled'}`,
                success: true,
            }));
        } else {
            // Revert
            setHintMap(prevMap);
            const msg = res.data?.detail || res.error?.message || 'Failed to update automation';
            dispatch(updateError({ message: msg, success: false }));
        }

        setInflight((prev) => {
            const next = new Set(prev);
            next.delete(workflowId);
            return next;
        });
    }, [practiceId, hintMap]);

    return { hintMap, loading, inflight, setEnabled };
}
